import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button, Input, Select, Textarea, Loader, Pill } from "@/components/ui/Primitives";
import { Sparkles, ChevronDown, ChevronUp, Check } from "lucide-react";
import { toast } from "sonner";

const PROVINCES = ["AB","BC","MB","NB","NL","NS","ON","PE","QC","SK","NT","NU","YT"];

/**
 * Modal-shaped wizard for generating an AI lease document from clauses.
 * Pass `lease` and `onGenerated(doc)` callback. Renders a single column wizard.
 */
export default function LeaseGenerator({ lease, onGenerated, onCancel }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [clauses, setClauses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [meta, setMeta] = useState({
    province: "ON",
    landlord_name: user?.name || "",
    tenant_name: lease?.tenant_email?.split("@")[0] || "",
    additional_occupants: "",
    custom_instructions: "",
  });

  // Sync landlord_name when user finishes loading
  useEffect(() => {
    if (user?.name && !meta.landlord_name) {
      setMeta((m) => ({ ...m, landlord_name: user.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.name]);

  // selected_clauses: array of { key, values: { fieldKey: value } }
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leases/clauses");
        const c = data?.clauses || data;
        setClauses(c);
        // Pre-select sensible defaults: insurance + late_fees + maintenance + quiet_hours
        const presets = ["insurance", "late_fees", "maintenance", "quiet_hours"];
        const init = {};
        for (const k of presets) {
          if (c[k]) {
            init[k] = defaultsFor(c[k]);
          }
        }
        setSelected(init);
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleClause = (key) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = defaultsFor(clauses[key]);
      return next;
    });
  };

  const setField = (key, fieldKey, value) => {
    setSelected((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [fieldKey]: value },
    }));
  };

  const submit = async () => {
    if (!meta.landlord_name.trim() || !meta.tenant_name.trim()) {
      toast.error("Landlord and tenant names are required.");
      setStep(1);
      return;
    }
    if (Object.keys(selected).length === 0) {
      toast.error("Select at least one clause.");
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        province: meta.province,
        landlord_name: meta.landlord_name.trim(),
        tenant_name: meta.tenant_name.trim(),
        additional_occupants: meta.additional_occupants
          ? meta.additional_occupants.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        selected_clauses: Object.entries(selected).map(([key, values]) => ({ key, values })),
        custom_instructions: meta.custom_instructions || null,
      };
      const { data } = await api.post(`/leases/${lease.lease_id}/generate-document`, body);
      toast.success("Lease document generated");
      onGenerated?.(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not generate document";
      toast.error(typeof detail === "string" ? detail : "Could not generate document");
    } finally { setSubmitting(false); }
  };

  if (loading) return <Loader/>;
  if (!clauses) return <div className="text-sm text-slate-500">Clauses could not be loaded.</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-green-800">
        <Sparkles className="w-4 h-4"/> Lease Generator
      </div>

      <Stepper step={step} onStep={setStep}/>

      {step === 1 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Province" value={meta.province} onChange={(e) => setMeta({ ...meta, province: e.target.value })} testid="gen-province">
              {PROVINCES.map((p) => <option key={p}>{p}</option>)}
            </Select>
          </div>
          <Input label="Landlord legal name" required value={meta.landlord_name} onChange={(e) => setMeta({ ...meta, landlord_name: e.target.value })} testid="gen-landlord-name"/>
          <Input label="Tenant legal name" required value={meta.tenant_name} onChange={(e) => setMeta({ ...meta, tenant_name: e.target.value })} testid="gen-tenant-name"/>
          <Input label="Additional occupants (comma-separated)" value={meta.additional_occupants} onChange={(e) => setMeta({ ...meta, additional_occupants: e.target.value })} placeholder="Spouse, child"/>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setStep(2)} data-testid="gen-step1-next">Next: choose clauses</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-2">
          <div className="text-sm text-slate-600 mb-3">
            <b>{Object.keys(selected).length}</b> of {Object.keys(clauses).length} clauses selected.
          </div>
          <div className="max-h-[380px] overflow-y-auto pr-1 space-y-2 -mx-1 px-1">
            {Object.entries(clauses).map(([key, def]) => (
              <ClauseRow
                key={key}
                ckey={key}
                def={def}
                values={selected[key]}
                expanded={!!expanded[key]}
                onToggle={() => toggleClause(key)}
                onExpand={() => setExpanded({ ...expanded, [key]: !expanded[key] })}
                onField={(f, v) => setField(key, f, v)}
              />
            ))}
          </div>
          <div className="flex justify-between pt-3">
            <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={() => setStep(3)} data-testid="gen-step2-next">Next: review</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Textarea label="Custom instructions (optional)" placeholder="Anything else you'd like included or emphasised…" value={meta.custom_instructions} onChange={(e) => setMeta({ ...meta, custom_instructions: e.target.value })} testid="gen-instructions"/>
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Summary</div>
            <div className="grid grid-cols-2 gap-y-1 text-sm">
              <div className="text-slate-500">Province</div><div className="font-semibold">{meta.province}</div>
              <div className="text-slate-500">Landlord</div><div className="font-semibold truncate">{meta.landlord_name}</div>
              <div className="text-slate-500">Tenant</div><div className="font-semibold truncate">{meta.tenant_name}</div>
              <div className="text-slate-500">Clauses</div><div className="font-semibold">{Object.keys(selected).length}</div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {Object.keys(selected).map((k) => (
                <Pill key={k} tone="green">{clauses[k]?.title || k}</Pill>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onCancel} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="gen-submit">
                <Sparkles className="w-4 h-4"/> {submitting ? "Generating…" : "Generate document"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function defaultsFor(def) {
  const out = {};
  for (const f of def.fields || []) {
    out[f.key] = f.default !== undefined ? f.default : (f.type === "boolean" ? false : f.type === "number" ? 0 : "");
  }
  return out;
}

function Stepper({ step, onStep }) {
  const steps = [
    { n: 1, label: "Parties" },
    { n: 2, label: "Clauses" },
    { n: 3, label: "Review" },
  ];
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const active = s.n === step;
        const done = s.n < step;
        return (
          <div key={s.n} className="flex items-center flex-1">
            <button
              type="button"
              onClick={() => onStep(s.n)}
              className={`flex items-center gap-2 flex-1 ${active ? "" : "opacity-70 hover:opacity-100"}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-green-800 text-white" : active ? "bg-green-800 text-white" : "bg-slate-200 text-slate-600"}`}>
                {done ? <Check className="w-3.5 h-3.5"/> : s.n}
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wider ${active ? "text-slate-900" : "text-slate-500"}`}>{s.label}</span>
            </button>
            {i < steps.length - 1 && <div className="w-6 h-px bg-slate-200 mx-1"/>}
          </div>
        );
      })}
    </div>
  );
}

function ClauseRow({ ckey, def, values, expanded, onToggle, onExpand, onField }) {
  const checked = !!values;
  return (
    <div className={`rounded-lg border ${checked ? "border-green-300 bg-green-50/50" : "border-slate-200 bg-white"} transition-colors`}>
      <div className="flex items-center justify-between p-3">
        <label className="flex items-start gap-3 flex-1 cursor-pointer min-w-0">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            data-testid={`gen-clause-${ckey}`}
            className="mt-0.5 w-4 h-4 rounded text-green-800 focus:ring-green-800"
          />
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-sm">{def.title}</div>
            <div className="text-xs text-slate-500 line-clamp-1">{def.description}</div>
          </div>
        </label>
        {checked && def.fields?.length > 0 && (
          <button type="button" onClick={onExpand} className="text-xs text-slate-500 hover:text-slate-900 font-semibold inline-flex items-center gap-1 p-1.5">
            {expanded ? <><ChevronUp className="w-3.5 h-3.5"/> Hide</> : <><ChevronDown className="w-3.5 h-3.5"/> Edit</>}
          </button>
        )}
      </div>
      {checked && expanded && (
        <div className="px-4 pb-4 pt-1 space-y-2 border-t border-slate-100">
          {(def.fields || []).map((f) => (
            <ClauseField key={f.key} field={f} value={values[f.key]} onChange={(v) => onField(f.key, v)}/>
          ))}
        </div>
      )}
    </div>
  );
}

function ClauseField({ field, value, onChange }) {
  if (field.type === "boolean") {
    return (
      <label className="flex items-center gap-2.5 text-sm text-slate-700 py-1">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded text-green-800"/>
        {field.label}
      </label>
    );
  }
  if (field.type === "enum") {
    return (
      <Select label={field.label} value={value || field.default || (field.options || [])[0] || ""} onChange={(e) => onChange(e.target.value)}>
        {(field.options || []).map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
      </Select>
    );
  }
  if (field.type === "number") {
    return (
      <Input label={field.label} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} placeholder={field.placeholder}/>
    );
  }
  if (field.type === "text" || !field.type) {
    return (
      <Input label={field.label} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder}/>
    );
  }
  return null;
}
